import {
  getGraphemeRender,
  getUnmappedCharacters,
  getVariationSequenceCodePoints,
  HANIVERSE_COMPOSITE_FONT_FAMILY,
  HANIVERSE_COMPOSITE_TC_FONT_FAMILY,
  variationSequenceDisplayText,
} from "@-/dev.improve.haniverse.assets.graphemes.fonts";
import type { HanziScript } from "@-/dev.improve.haniverse.lib.actor.types";
import type { GraphemeGlyphFontWeight } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.entities";
import { Platform } from "@-/com.npmjs.react-native";

export const HANIVERSE_COMPOSITE = HANIVERSE_COMPOSITE_FONT_FAMILY;
export const HANIVERSE_COMPOSITE_TC = HANIVERSE_COMPOSITE_TC_FONT_FAMILY;

/**
 * The traditional face carries the Noto TC repertoire across the weight axis. Native fallback
 * chains and the web stack supply SC-only, Latin and extension glyphs from Han Composite.
 */
export const HANIVERSE_COMPOSITE_TC_WEB_FONT_STACK = `"${HANIVERSE_COMPOSITE_TC}", "${HANIVERSE_COMPOSITE}"`;

export type FontStyleForGrapheme = { fontFamily: string };

function firstScalarCharacter(grapheme: string) {
  const codePoint = grapheme.codePointAt(0);
  return codePoint === undefined ? grapheme : String.fromCodePoint(codePoint);
}

/** The Han Composite family that draws grapheme glyphs for the active hanzi script. */
export function graphemeFontFamily(script: HanziScript): string {
  if (script !== "traditional") {
    return HANIVERSE_COMPOSITE;
  }
  return Platform.OS === "web" ? HANIVERSE_COMPOSITE_TC_WEB_FONT_STACK : HANIVERSE_COMPOSITE_TC;
}

/**
 * The font style for a grapheme, or `null` when no bundled face draws it (the renderer then avoids
 * drawing tofu). Every drawable grapheme renders with Han Composite — which SOURCE face supplied
 * its outline is a build-time decision recorded on the card, not something the runtime picks.
 */
export function fontStyleForGrapheme(
  grapheme: string,
  script: HanziScript,
): FontStyleForGrapheme | null {
  // There is no glyph to style, so there is no font to pick. Checked explicitly: the previous
  // implementation got this for free from `unmapped.includes("")` being vacuously true on a
  // substring search, which a Set membership test does not reproduce.
  if (grapheme === "") {
    return null;
  }

  // The `unmapped` short-circuit is keyed on the bare base character, so an unmapped base with a
  // stray selector is still treated as undrawable.
  if (getUnmappedCharacters().has(firstScalarCharacter(grapheme))) {
    return null;
  }

  return { fontFamily: graphemeFontFamily(script) };
}

/** Request the real weight axis on native and web, including non-named intermediate weights. */
export function graphemeFontWeightStyle(fontWeight: GraphemeGlyphFontWeight) {
  return { fontVariationSettings: "'wght' " + fontWeight, fontWeight };
}

/**
 * The text to actually RENDER for `grapheme`. Han Composite carries no cmap format-14 (UVS)
 * subtable, so an authored variation sequence — the left-falling dot `㇔︀` (U+31D4 U+FE00), the
 * rising hook `㇆︁`, the Jigmo2 IVS `𦰩󠄂` (U+26C29 U+E0102) — is rewritten to the private-use code
 * point the font build homed its variant glyph at (allocated identically on both sides by
 * `allocateVariationSequenceCodePoints`). Selecting the font family alone would leave the BASE
 * glyph on screen.
 *
 * Rewriting is per SEQUENCE, over the whole string: a mnemonic decomposition like `⿱人㇔︀㇔︀` leads
 * with an ideographic-description character, so a leading-unit check would skip the rewrite
 * entirely. A sequence no bundled face draws passes through rather than becoming tofu.
 *
 * A card may also declare an outright substitute via its study `render.unicode:` — the codepoint to
 * DRAW in place of its own, for a glyph whose reference form has no acceptable outline in anything
 * we bundle. That substitution happens here and only here, so the reference codepoint is what every
 * other consumer keeps seeing: copying, searching, twin pairing and persistence all stay on the
 * character the card is actually about, and only the shaper is handed the stand-in.
 *
 * Deliberately script-independent: which private-use code point carries a variant is a property of
 * the bundled font, not of simplified/traditional. The parameter is kept so callers can stay
 * symmetric with `fontStyleForGrapheme`.
 */
export function graphemeDisplayText(grapheme: string, _script: HanziScript): string {
  const substitute = getGraphemeRender(grapheme)?.unicode;
  const drawn = substitute === undefined || substitute === "" ? grapheme : substitute;
  return variationSequenceDisplayText(drawn, getVariationSequenceCodePoints());
}
